import { FC, useMemo, useEffect, useContext } from 'react';
import { graphql, createRefetchContainer, RelayRefetchProp } from 'react-relay/legacy';
import { useSelector } from 'react-redux';
import Helmet from 'react-helmet';

import { RecentlyViewedItemsProvider } from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';
import { ServerVarsContext } from '../global/ServerVarsContext/ServerVarsContext';
import { PdpRespContainer } from './PdpRespContainer/PdpRespContainer';
import { PdpSharedLayout } from './PdpSharedLayout';
import {
    useShippingType,
    getShippingTypeTracking,
} from './PdpSharedShippingInfo/hooks/useShippingType';
import { excludeShippingPromos } from './PdpSharedShippingInfo/excludeShippingPromos';
import { useRenderBlurbs } from './PdpRespSidebarBlurbs/hooks/useRenderBlurbs';
import { PdpSharedAuctionContextProvider } from './PdpSharedAuctionContext/PdpSharedAuctionContext';

import { PdpRespLayout_viewer$data } from './__generated__/PdpRespLayout_viewer.graphql';
import { PdpRespLayoutRefetchQuery$variables } from './__generated__/PdpRespLayoutRefetchQuery.graphql';

import './styles/PdpRespLayout.scss';

type ReduxState = {
    relayVariables: {
        variables: PdpRespLayoutRefetchQuery$variables;
    };
    pdpPreviewPage: {
        isPdpRespPreviewPage: boolean;
    };
    currency: {
        currency: string;
        showCurrency: boolean;
    };
};

export const PdpRespLayoutComponent: FC<{
    viewer: PdpRespLayout_viewer$data;
    relay: RelayRefetchProp;
}> = ({ viewer, relay }) => {
    const { isPdpMetaDescRemovalVariant, isTablet, isPdpSidebarRemovePaidShipping } = useContext(
        ServerVarsContext
    );
    const { isPdpRespPreviewPage, isClient, curr } = useSelector((state: ReduxState) => ({
        isPdpRespPreviewPage: state?.pdpPreviewPage?.isPdpRespPreviewPage,
        isClient: state?.relayVariables?.variables?.isClient,
        curr: state?.currency?.currency,
    }));

    useEffect(() => {
        if (isPdpRespPreviewPage) {
            document.body.classList.add('disable-click-on-preview');
        }
    }, [isPdpRespPreviewPage]);

    const recentlyViewedAuctionCountdownFF = viewer.recentlyViewedAuctionCountdownFF || false;
    const similarLandingItems = viewer.similarLandingItems || null;
    const isShippingPromotion = viewer.activePromotion?.isShippingPromotion;
    const clickabilityRerank =
        viewer.similarLandingItems?.metadata?.isUsingRerankedResults || false;
    const personalizationType = viewer.personalizedItems?.associationType;

    const user = viewer.user || null;

    const item = viewer.itemRead?.[0] || null;
    const displayInfo = item?.displayInfo || null;

    const includeShippingData = isClient && isPdpSidebarRemovePaidShipping;

    const shippingType = useShippingType({
        viewer: includeShippingData ? viewer : null,
        item: includeShippingData ? item : null,
        currency: curr,
    });
    const renderBlurbs = useRenderBlurbs({ item, displayInfo });

    const additionalTrackingData = useMemo(() => {
        const trackingData: Record<string, string | boolean> = {
            isCondensed: 'N',
            clickabilityRerank,
            personalization: personalizationType ? `true - ${personalizationType}` : 'false',
        };

        if (renderBlurbs && isPdpSidebarRemovePaidShipping) {
            trackingData.shippingType = getShippingTypeTracking(
                isShippingPromotion && !excludeShippingPromos(shippingType)
                    ? 'shipping_promo'
                    : shippingType
            );
        }

        return trackingData;
    }, [
        shippingType,
        renderBlurbs,
        isShippingPromotion,
        isPdpSidebarRemovePaidShipping,
        clickabilityRerank,
        personalizationType,
    ]);

    if (!item) {
        return null;
    }

    return (
        <RecentlyViewedItemsProvider
            excludedItemId={item.serviceId}
            maxItems={isTablet ? 10 : 12}
            itemsPerPage={isTablet ? 5 : 6}
            recentlyViewedAuctionCountdownFF={recentlyViewedAuctionCountdownFF}
        >
            <PdpSharedAuctionContextProvider>
                <PdpSharedLayout
                    user={user}
                    viewer={viewer}
                    refreshPageOnTradeLogin={false}
                    additionalTrackingData={additionalTrackingData}
                    doRefetch={relay.refetch}
                    environment={relay.environment}
                >
                    {({ pdpHead, currency, onSkuChange, selectedSkuId }: $TSFixMe) => (
                        <>
                            <Helmet
                                {...pdpHead.getPdpData({
                                    hideDescription: isPdpMetaDescRemovalVariant,
                                })}
                            />
                            <PdpRespContainer
                                viewer={viewer}
                                user={user}
                                item={item}
                                itemSearch={null}
                                currency={currency}
                                onSkuChange={onSkuChange}
                                displayInfo={displayInfo}
                                selectedSkuId={selectedSkuId}
                                similarLandingItems={similarLandingItems}
                            />
                        </>
                    )}
                </PdpSharedLayout>
            </PdpSharedAuctionContextProvider>
        </RecentlyViewedItemsProvider>
    );
};

//TODO: refactor to useRefechableFragment when PdpSharedLayout is converted to typescript
export const PdpRespLayout = createRefetchContainer(
    PdpRespLayoutComponent,
    {
        viewer: graphql`
            fragment PdpRespLayout_viewer on Viewer {
                ...PdpRespContainer_viewer
                ...PdpSharedLayout_viewer
                recentlyViewedAuctionCountdownFF: featureFlag(
                    feature: "recentlyViewedAuctionCountdown"
                )
                user(userId: $userId) @include(if: $fetchUser) {
                    ...PdpRespContainer_user
                    ...PdpSharedLayout_user
                }
                itemRead(itemIds: $itemIds) {
                    ... on Item {
                        serviceId
                        ...PdpRespContainer_item
                        displayInfo(page: pdp, buyerId: $userId) @include(if: $isClient) {
                            ...PdpRespContainer_displayInfo
                            ...useRenderBlurbs_displayInfo
                        }
                        ...useShippingType_item
                            @arguments(userId: $userId)
                            @include(if: $isPdpSidebarRemovePaidShipping)
                        ...useRenderBlurbs_item
                    }
                }
                similarLandingItems: moreLikeThisItem(
                    first: 12
                    itemId: $itemId
                    rerankDocs: $clickabilityRerankDocs
                    rerankQueryTemplate: $clickabilityRerankQueryTemplate
                    clickabilityVariant: $clickabilityVariant
                    isTrade: $isTrade
                ) @include(if: $fetchPdp) {
                    metadata {
                        isUsingRerankedResults
                    }
                    ...PdpRespContainer_similarLandingItems
                }
                personalizedItems(itemId: $itemId)
                    # TOF-419: Remove directive at cleanup and replace with @include(if: $fetchPdp)
                    @include(if: $personalizationModuleEnabled) {
                    associationType
                }

                activePromotion(userId: $userId, itemId: $itemId)
                    @include(if: $isPdpSidebarRemovePaidShipping) {
                    isShippingPromotion
                }
                ...useShippingType_viewer
                    @arguments(
                        userId: $userId
                        itemId: $itemId
                        itemQuantity: $itemQuantity
                        userZipCode: $userZipCode
                        userCountryCode: $userCountryCode
                    )
                    @include(if: $isPdpSidebarRemovePaidShipping)
            }
        `,
    },
    graphql`
        query PdpRespLayoutRefetchQuery(
            $isClient: Boolean!
            $itemId: String!
            $itemIds: [String]!
            $userId: String = ""
            $userIds: [String]
            $fetchUser: Boolean!
            $followSearchPages: [String]
            $fetchFollowSearch: Boolean!
            $isTrade: Boolean!
            $isMultiSku: Boolean!
            $fetchPdp: Boolean!
            $forcedCurrency: Currency
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $localRecentHistoryItemIds: [String] = []
            $fetchUserRecentHistoryItem: Boolean = false
            $isInteractivePdpChat: Boolean = false
            $timezoneOffset: Int
            $fetchAuction: Boolean = false
            $clickabilityRerankDocs: Int = null
            $clickabilityRerankQueryTemplate: RerankQueryTemplate = null
            $clickabilityVariant: ClickabilityVariant = null
            # VERTICALS-1194: resp PDP redesign test
            $isPdpRedesignVariant: Boolean = false
            $currency: Currency
            $isPdpSidebarRemovePaidShipping: Boolean = false
            $itemQuantity: Int
            # TOF-419: Add personalization module
            $personalizationModuleEnabled: Boolean = false
            # TOF-471: Restyle PDP product tiles
            $hasRestyledProductTiles: Boolean = false
        ) {
            viewer {
                ...PdpRespLayout_viewer
            }
        }
    `
);
